import {WonderlandEngine} from '../engine.js';
import {ParticleEffect, ParticleEffectParameters} from '../wonderland.js';
import {ResourceManager} from './resource.js';

/**
 * Manage particle effects.
 *
 * #### Creation
 *
 * Creating a particle effect is done using {@link ParticleEffectManager.create}:
 *
 * ```js
 * const particleEffect = engine.particleEffects.create({maxCount: 10, mesh: myMesh, material: myMaterial});
 * ```
 *
 * @since 1.5.0
 */
export class ParticleEffectManager extends ResourceManager<ParticleEffect> {
    constructor(engine: WonderlandEngine) {
        super(engine, ParticleEffect);
    }

    /**
     * Create a new particle effect.
     *
     * @param params Particle effect settings. For more information, have a
     *     look at the {@link ParticleEffectParameters} object.
     */
    create(params: Partial<ParticleEffectParameters>) {
        if (!params.maxCount) throw new Error("Missing parameter 'maxCount'");
        if (params.maxCount < 0 || params.maxCount > 65535)
            throw new Error("'maxCount' must be between 0 and 65535");

        const index = this.engine.wasm._wl_particleEffect_create(
            params.maxCount,
            params.mesh?._id ?? 0,
            params.material?._id ?? 0,
            params.colors ?? false,
            params.instanceData ?? false
        );
        const instance = new ParticleEffect(this._host, index);
        this._cache[instance.index] = instance;
        return instance;
    }
}
